<script setup lang="ts">
const { getSchoolInfo } = useSchool()
const { data: info } = await useAsyncData('school-history', () => getSchoolInfo())
const history = computed(() => info.value?.history || [])

const isVertical = ref(false)
const checkLayout = () => {
  isVertical.value = window.innerWidth < 1280
}

onMounted(() => {
  checkLayout()
  window.addEventListener('resize', checkLayout)
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', checkLayout)
  }
})

const flipped = ref<number | null>(null)
const toggleFlip = (index: number) => {
  if (isVertical.value) {
    flipped.value = flipped.value === index ? null : index
  }
}

const { elementRef: sectionRef, isVisible: isSectionVisible } = useScrollReveal()
</script>

<template>
  <section id="histoire" ref="sectionRef" class="py-section-py bg-background text-foreground relative overflow-hidden transition-colors duration-300">
    <div class="container mx-auto px-container-px relative z-10 transition-all duration-1000" :class="isSectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'">
      <UiSectionHeader 
        titlePrefix="Notre"
        titleHighlight="Histoire"
        subtitle="De la première étincelle à l'expertise d'aujourd'hui : le parcours d'une vision."
        class="mb-section-header-mb"
      />

      <div class="relative max-w-6xl mx-auto">
        <!-- Vertical Timeline Line -->
        <div 
          class="absolute left-8 xl:left-1/2 transform xl:-translate-x-1/2 w-[2px] bg-linear-to-b from-transparent via-oxo-border to-transparent origin-top h-full"
        >
        </div>

        <!-- Timeline Items -->
        <div 
          v-for="(item, index) in history" 
          :key="index"
          class="relative flex flex-col xl:flex-row items-center justify-between mb-24 w-full group"
        >
          <!-- DESKTOP: Description Side -->
          <div 
            class="hidden xl:flex w-[42%] items-center"
            :class="Number(index) % 2 === 0 ? 'justify-end pr-8' : 'order-last justify-start pl-8'"
          >
            <p class="text-foreground-dim text-lg font-light leading-relaxed max-w-sm" :class="Number(index) % 2 === 0 ? 'text-right' : 'text-left'">
              {{ item.description }}
            </p>
          </div>

          <!-- Timeline Center Node (Year) -->
          <div class="absolute left-0 xl:left-1/2 top-1/2 transform -translate-y-1/2 translate-x-0 xl:-translate-x-1/2 flex items-center justify-center z-20">
            <div class="absolute inset-0 bg-oxo-red opacity-0 group-hover:opacity-40 xl:group-hover:opacity-0 rounded-full blur-md animate-ping xl:animate-none"></div>
            <div class="relative w-16 h-16 bg-background border-4 border-oxo-border rounded-full flex items-center justify-center shadow-lg group-hover:border-oxo-red xl:group-hover:border-oxo-border transition-all duration-300 group-hover:scale-110 xl:group-hover:scale-100">
              <span class="text-base font-black text-foreground group-hover:text-oxo-red xl:group-hover:text-foreground transition-colors duration-300">{{ item.year }}</span>
            </div>
          </div>

          <!-- Flip Card Container (MOBILE: FLIPPABLE, DESKTOP: STATIC) -->
          <div 
            class="w-full xl:w-[42%] pl-24 xl:pl-0 h-[180px] xl:h-[220px] perspective-1000"
            :class="isVertical ? 'cursor-pointer' : 'cursor-default'"
            @click="toggleFlip(Number(index))"
          >
            <div 
              class="relative w-full h-full transition-transform duration-700 preserve-3d"
              :class="{ 
                'rotate-y-180 xl:rotate-y-0': flipped === Number(index) 
              }"
            >
              <!-- Front Side (Title) -->
              <div class="absolute inset-0 backface-hidden">
                <div 
                  class="h-full relative bg-oxo-card-bg/20 backdrop-blur-xl p-8 rounded-4xl border border-oxo-border shadow-xl transition-all duration-300 group-hover:bg-oxo-card-bg/40 xl:group-hover:bg-oxo-card-bg/20 group-hover:border-oxo-border/50 xl:group-hover:border-oxo-border flex flex-col items-center justify-center overflow-hidden"
                >
                  <h3 class="font-display font-black text-xl xl:text-3xl text-foreground mb-3 group-hover:text-oxo-yellow xl:group-hover:text-foreground transition-colors duration-300 text-center uppercase tracking-wider">
                    {{ item.title }}
                  </h3>
                  
                  <!-- Mobile only flip indicator -->
                  <div class="xl:hidden text-center text-oxo-red/40 group-hover:text-oxo-red transition-colors duration-300">
                    <i class="fas fa-info-circle text-sm animate-pulse" aria-hidden="true"></i>
                  </div>
                  
                  <div class="absolute bottom-0 left-0 h-[3px] bg-linear-to-r from-oxo-red to-oxo-yellow w-0 group-hover:w-full xl:group-hover:w-0 transition-all duration-500 ease-out"></div>
                </div>
              </div>

              <!-- Back Side (Description - Mobile ONLY) -->
              <div class="absolute inset-0 backface-hidden rotate-y-180 xl:hidden">
                <div class="h-full relative bg-oxo-card-bg p-8 rounded-4xl border border-oxo-red/30 flex items-center justify-center text-center shadow-2xl overflow-hidden">
                  <div class="absolute top-0 left-0 w-full h-1 bg-oxo-red"></div>
                  <p class="text-white text-sm font-light leading-relaxed">
                    {{ item.description }}
                  </p>
                  <div class="absolute bottom-4 right-6 text-oxo-red/30 italic text-[10px] uppercase tracking-widest">
                    <i class="fas fa-redo mr-1" aria-hidden="true"></i> Retour
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.perspective-1000 {
  perspective: 1000px;
}
.preserve-3d {
  transform-style: preserve-3d;
}
.backface-hidden {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  /* Firefox fix: force GPU layer */
  transform: translateZ(0);
}
.rotate-y-180 {
  transform: rotateY(180deg);
}

/* Firefox-specific fix for front face when flipped */
.rotate-y-180 .backface-hidden:first-child {
  visibility: hidden;
}
.rotate-y-180 .backface-hidden:last-child {
  visibility: visible;
}
</style>
